import { normalizeBrandKey, hostBrandLabel } from './brand-matching';
import { normalizeHost } from './favicon';

/**
 * Lookup table: normalized brand name / host label → website host.
 * Built once from project domains, aliases, and competitors so logos
 * can resolve even when an API row only has a brand name.
 */
export function buildBrandHostRegistry(input: {
  domains?: Array<{ host: string; isPrimary?: boolean; isTrackingSource?: boolean }>;
  brandAliases?: Array<{ name: string }>;
  competitors?: Array<{
    host: string;
    brandName: string;
    aliases?: Array<{ name: string }>;
  }>;
  /** Extra known hosts keyed by display name (e.g. from visibility rows). */
  extraBrands?: Array<{ name: string; host?: string | null }>;
  /** Project / product display names that should map to the primary domain. */
  ownBrandNames?: Array<string | null | undefined>;
}): Map<string, string> {
  const map = new Map<string, string>();

  const set = (name: string | null | undefined, host: string | null | undefined) => {
    const normalizedName = name ? normalizeBrandKey(name) : '';
    const normalizedHost = host ? normalizeHost(host) : '';
    if (!normalizedName || !normalizedHost) return;
    if (!map.has(normalizedName)) map.set(normalizedName, normalizedHost);
  };

  const trackingDomains = (input.domains ?? []).filter(
    (domain) => domain.isTrackingSource !== false,
  );
  const primary =
    trackingDomains.find((domain) => domain.isPrimary) ?? trackingDomains[0] ?? null;
  const primaryHost = primary?.host ? normalizeHost(primary.host) : '';

  if (primaryHost) {
    set(primaryHost, primaryHost);
    set(hostBrandLabel(primaryHost), primaryHost);
    for (const alias of input.brandAliases ?? []) {
      set(alias.name, primaryHost);
    }
    for (const name of input.ownBrandNames ?? []) {
      set(name, primaryHost);
    }
  }

  for (const domain of trackingDomains) {
    const host = normalizeHost(domain.host);
    if (!host) continue;
    set(host, host);
    set(hostBrandLabel(host), host);
  }

  for (const competitor of input.competitors ?? []) {
    const host = normalizeHost(competitor.host);
    if (!host) continue;
    set(competitor.brandName, host);
    set(host, host);
    set(hostBrandLabel(host), host);
    for (const alias of competitor.aliases ?? []) {
      set(alias.name, host);
    }
  }

  for (const brand of input.extraBrands ?? []) {
    set(brand.name, brand.host);
  }

  return map;
}

export function resolvePrimaryTrackingHost(
  domains?: Array<{ host: string; isPrimary?: boolean; isTrackingSource?: boolean }>,
): string | null {
  const tracking = (domains ?? []).filter((domain) => domain.isTrackingSource !== false);
  const primary = tracking.find((domain) => domain.isPrimary) ?? tracking[0] ?? null;
  return primary?.host ? normalizeHost(primary.host) : null;
}

export function isOwnBrandLogoHint(
  relationship?: string | null,
  brandType?: string | null,
): boolean {
  return relationship === 'you' || brandType === 'own_brand';
}

export function resolveHostFromRegistry(
  registry: Map<string, string> | null | undefined,
  name?: string | null,
  host?: string | null,
  options?: {
    relationship?: string | null;
    brandType?: string | null;
    ownBrandHost?: string | null;
  },
): string | null {
  const fromHost = host ? normalizeHost(host) : '';
  if (fromHost) return fromHost;

  if (
    options?.ownBrandHost &&
    isOwnBrandLogoHint(options.relationship, options.brandType)
  ) {
    return normalizeHost(options.ownBrandHost);
  }

  if (!registry || !name) return null;

  const key = normalizeBrandKey(name);
  if (!key) return null;
  return registry.get(key) ?? registry.get(hostBrandLabel(name)) ?? null;
}
